import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ReCAPTCHA from "react-google-recaptcha";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const recaptchaSiteKey = import.meta.env.VITE_GOOGLE_CAPTCHA_KEY;

let apiUrl = "https://api.gimmefy.ai/v2";

console.log("import.meta.env.VITE_API_URL", import.meta.env.VITE_API_URL);
const baseUrl = window.location.origin;
if (baseUrl.includes("localhost") || baseUrl.includes("127.0.0.1") || baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}
interface BrandAnalyzerFormProps {
  onSuccess?: () => void;
}

const BrandAnalyzerForm = ({ onSuccess }: BrandAnalyzerFormProps = {}) => {
  const { toast } = useToast();
  const [companyUrl, setCompanyUrl] = useState("");
  const [name, setName] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle reCAPTCHA change
  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const validateForm = () => {
    if (!name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter your name",
        variant: "destructive"
      });
      return false;
    }
    if (!workEmail.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter your work email",
        variant: "destructive"
      });
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(workEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return false;
    }

    if (!companyUrl.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter your company URL",
        variant: "destructive"
      });
      return false;
    }

    // Basic URL validation
    try {
      new URL(companyUrl.startsWith('http') ? companyUrl : `https://${companyUrl}`);
    } catch {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL (e.g., example.com)",
        variant: "destructive"
      });
      return false;
    }

    if (!captchaValue) {
      toast({
        title: "reCAPTCHA Error",
        description: "Please complete the reCAPTCHA verification",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);

    try {
      const normalizedUrl = companyUrl.startsWith('http') ? companyUrl : `https://${companyUrl}`;
      const payload = {
        event_name: "USER_CONTACT_FROM_BRAND_ANALYZER_FORM",
        payload: {
          company_url: normalizedUrl,
          name: name,
          work_email: workEmail,
          timestamp: new Date().toISOString()
        },
        captcha_code: captchaValue
      };

      const response = await fetch(`${apiUrl}/triggerNotificationPublic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }

      toast({
        title: "Analysis Request Received!",
        description: "We'll email your report soon.",
      });

      // Reset form and reCAPTCHA
      setCompanyUrl("");
      setName("");
      setWorkEmail("");
      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("Error sending brand analyzer request:", err);
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div className="grid md:grid-cols-2 gap-3 md:gap-4">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-1 p-0 sm:p-1"
          >
            <Label htmlFor="name" className="text-xs sm:text-sm font-semibold">
              Your Name *
            </Label>
            <Input 
              id="name" 
              type="text" 
              placeholder="Your Name" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              className="h-10 sm:h-11 text-sm sm:text-base transition-all focus:ring-2 focus:ring-primary/20" 
              required 
            />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-1 p-0 sm:p-1"
          >
            <Label htmlFor="workEmail" className="text-xs sm:text-sm font-semibold">
              Your Email *
            </Label>
            <Input 
              id="workEmail" 
              type="email" 
              placeholder="you@company.com" 
              value={workEmail} 
              onChange={e => setWorkEmail(e.target.value)} 
              className="h-10 sm:h-11 text-sm sm:text-base transition-all focus:ring-2 focus:ring-primary/20" 
              required 
            />
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-1 p-0 sm:p-1"
        >
          <Label htmlFor="companyUrl" className="text-xs sm:text-sm font-semibold">
            Company URL *
          </Label>
          <Input 
            id="companyUrl" 
            type="text" 
            placeholder="example.com" 
            value={companyUrl} 
            onChange={e => setCompanyUrl(e.target.value)} 
            className="h-10 sm:h-11 text-sm sm:text-base transition-all focus:ring-2 focus:ring-primary/20" 
            required 
          />
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-2"
        >
          <div className="flex justify-center min-h-[60px] sm:min-h-[70px] md:min-h-[78px] transform scale-75 sm:scale-90 md:scale-100">
            <ReCAPTCHA
              id="brand-analyzer-recaptcha"
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              onChange={handleCaptchaChange}
              theme="light"
            />
          </div>
          {!captchaValue && (
            <p className="text-xs text-muted-foreground text-center">
              Please complete the reCAPTCHA verification above
            </p>
          )}
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          whileHover={{ scale: 1.02 }} 
          whileTap={{ scale: 0.98 }}
        >
          <Button 
            type="submit" 
            variant="hero" 
            size="lg" 
            className="w-full h-11 sm:h-12 text-sm sm:text-base md:text-lg font-semibold group shadow-lg hover:shadow-xl transition-all" 
            disabled={isSubmitting || !captchaValue}
          >
            {isSubmitting ? "Submitting..." : "Get My Free Report"}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>
      </form>
  );
};
export default BrandAnalyzerForm;